// Composes the existing `DialogWrapper` + `Atlas/EditSphere` rather than introducing a new
// dialog: this molecule is a de-duplication of the identical edit-sphere open/close/save
// handlers that MetaspaceGrid and SpaceHost each carried, not a fork of either component.
import React from 'react';
import { useTranslation } from 'localization';
import DialogWrapper from '../DialogWrapper/DialogWrapper';
import EditSphere from '../Atlas/EditSphere/EditSphere';
import type { FetchPermissionList } from './useEditSphereLauncher';
import type { Sphere as GlobalSphere } from 'types/global';
import './EditSphereLauncherMolecule.scss';

export interface EditSphereLauncherMoleculeProps {
  sphereToEdit: GlobalSphere | null;
  visible: boolean;
  onSave: (updatedSphere: GlobalSphere) => void;
  /** Called when the sphere changed while the editor stays open (its glyph, colour or picture). */
  onSphereRefresh?: (sphereId: string) => void;
  onClose: () => void;
  fetchPermissionList: FetchPermissionList;
}

/**
 * The dialog half of `useEditSphereLauncher` — extracted from `MetaspaceGrid`'s inline
 * `EditSphere`/`DialogWrapper` JSX (spec § Phase R4 task 1) so both the legacy grid and `SpaceHost`
 * render the same edit-sphere affordance instead of carrying two divergent copies of the dialog.
 */
const EditSphereLauncherMolecule: React.FC<EditSphereLauncherMoleculeProps> = ({
  sphereToEdit,
  visible,
  onSave,
  onSphereRefresh,
  onClose,
  fetchPermissionList,
}) => {
  const { t } = useTranslation();

  if (!sphereToEdit) return null;

  return (
    <DialogWrapper
      header={t('sphere.editor.editHeader')}
      visible={visible}
      onHide={onClose}
      className="edit-sphere-launcher-dialog"
    >
      <EditSphere
        sphere={sphereToEdit}
        parentSphere={sphereToEdit}
        onSave={onSave}
        onSphereRefresh={onSphereRefresh}
        onClose={onClose}
        fetchPermissionList={fetchPermissionList}
        skipInitialFetch={true}
      />
    </DialogWrapper>
  );
};

export default EditSphereLauncherMolecule;
